import React from 'react'
import { string, bool, func } from 'prop-types'
import styled, { css } from 'styled-components'
import LazyLoad from 'react-lazyload'

import * as spacing from 'styles/spacing'
import { white } from 'styles/colors'
import { media } from 'styles/media'
import { borderRadius } from 'styles/global'
import { H1, H2, fontSizes, fontWeights, Lead } from 'styles/typography'

import { Action } from 'components/home/FeaturedProduct'
import Button from 'components/button/Button'

const Container = styled.div`
  position: relative;
  padding: ${spacing.xLarge};
  background: ${props => props.background};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  width: 100%;

  ${media.xLarge`
    padding: ${spacing.xxLarge};
  `};
`

const ImageWrap = styled.div`
  position: relative;
  z-index: 10;
  width: 55%;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 2.5vw;
  min-width: 300px;

  ${media.large`
    min-width: 330px;
  `};

  ${media.xLarge`
    min-width: 350px;
  `};

  ${media.xxLarge`
    margin-bottom: 2vw;
    min-width: 420px;
    max-width: 540px;
  `};
`

const Image = styled.img`
  position: static;

  ${props =>
    props.isFlavour &&
    css`
      border-radius: 50%;
      filter: drop-shadow(3px 6px 20px rgba(0, 0, 0, 0.3));
    `}
`

const Name = styled(H1)`
  display: block;
  font-family: 'Trim Poster';
  text-transform: uppercase;
  color: ${white};
  letter-spacing: 0.05em;
  line-height: 1;
  position: relative;
  z-index: 10;
  margin-bottom: 0.2em;

  ${media.medium`
    font-size: 56px;
  `};

  ${media.large`
    font-size: 64px;
  `};

  ${media.xLarge`
    font-size: 72px;
  `};
`

const ActionWrap = styled.div`
  position: relative;
  z-index: 10;

  ${media.medium`
    position: absolute;
    top: ${spacing.xLarge};
    right: ${spacing.xLarge};
  `};

  ${media.large`
    top: ${spacing.xxLarge};
    right: ${spacing.xxLarge};
  `};

  ${media.xLarge`
    top: ${spacing.xxxLarge};
    right: ${spacing.xxxLarge};
  `};
`

const Description = styled(Lead)`
  color: ${white};

  ${media.medium`
    height: 54px;
  `};

  ${media.large`
    height: 56px;
  `};
`

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
  z-index: 10;
`

const Price = styled(H2)`
  color: ${white};
  display: block;
  margin: 0;
`

const Tag = styled.span`
  display: inline-block;
  background-color: #b00020;
  color: ${white};
  text-transform: uppercase;
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.medium};
  padding: 4px 10px;
  min-width: 80px;
  text-align: center;
  margin-bottom: ${spacing.medium};
  border-radius: ${borderRadius};
  opacity: 0;

  ${props =>
    props.visible &&
    css`
      opacity: 1;
    `}
`

const Background = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  mix-blend-mode: screen;
  opacity: 0.1;
  pointer-events: none;
`

const Featured = ({ name, description, background, link, price, handleCtaClick, ctaLabel, slug, isNew }) => {
  const isFlavour = slug !== 'nicotine-free'

  return (
    <Container background={background}>
      <ImageWrap>
        <LazyLoad>
          <Image
            src={`${process.env.BASE_PATH}/images/home/offers/flavours/${slug}.png`}
            isFlavour={isFlavour}
            alt={name}
          />
        </LazyLoad>
      </ImageWrap>
      <Tag visible={isNew}>New</Tag>
      <Name as="span">
        <span dangerouslySetInnerHTML={{ __html: name }} />
      </Name>
      <Description>{description}</Description>
      <Footer>
        <Button to={link} theme="custom" customColor={white} variant="outline" size="large">
          Buy now
        </Button>

        <Price as="span">{price}</Price>
      </Footer>
      <ActionWrap>
        <Action label={ctaLabel} onClick={handleCtaClick} />
      </ActionWrap>
      <Background src={`${process.env.BASE_PATH}/images/home/offers/shape.svg`} />
    </Container>
  )
}

Featured.propTypes = {
  name: string.isRequired,
  description: string.isRequired,
  background: string.isRequired,
  ctaLabel: string.isRequired,
  price: string.isRequired,
  slug: string.isRequired,
  isNew: bool,
  link: string.isRequired,
  handleCtaClick: func.isRequired
}

export default Featured
